import {
  Badge,
  Box,
  Chip,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import DifferenceOutlinedIcon from "@mui/icons-material/DifferenceOutlined";
import { useTranslation } from "react-i18next";
import { useCallback, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AdminReviewSessionViewDto, GfFlyerReviewChangeRequest } from "@swagger/typescript-fetch-goflyer";
import { messages } from "../../../../locales/messages";
import { toastSuccess } from "../../../../utils/pageUtil/toast";
import { reviewApiService } from "../../../../utils/api";
import { handleReviewError } from "../errorHandling";
import { HistoryTarget, ReviewChangeRequest, toAcceptedCr } from "../types";
import {
  collectCrAffectedItemIds,
  invalidateFlyerItemCaches,
} from "../invalidateReviewCache";
import { ItemActivityGroup } from "./ChangesByItem/useReviewActivity";
import { ItemSummary } from "./ChangesByItem/useFlyerItemSummaries";
import { ChangeRequestList } from "./ChangesByItem/ChangeRequestList";
import { ItemCommentsList } from "./ChangesByItem/ItemCommentsList";
import { CrHistoryDrawer } from "./ChangesByItem/CrHistoryDrawer";
import { AppliedRevisionPreview } from "./ChangesByItem/AppliedRevisionPreview";
import { HistoryTree } from "./ChangesByItem/HistoryTree";
import { buildHistorySlots } from "./ChangesByItem/buildHistorySlots";
import { useCrSubmitter } from "./ChangesByItem/useCrSubmitter";
import { reviewTokens } from "../theme";

const CrSt = GfFlyerReviewChangeRequest.StatusEnum;

type StatusFilter = "ALL" | GfFlyerReviewChangeRequest.StatusEnum | "HISTORY";

interface Props {
  flyerId: string;
  reqOptions: { headers: { Authorization: string } };
  bootstrap: AdminReviewSessionViewDto | undefined;
  flyerPublicationStatus?: string;
  selectedFlyerItemId?: string;
  groups: ItemActivityGroup[];
  summaries: Map<string, ItemSummary | "loading">;
  busyIds: string[];
  setBusy: (id: string, busy: boolean) => void;
  onActivityRefetch: () => Promise<void>;
}

interface StatusFilterDef {
  id: StatusFilter;
  labelKey: () => ReturnType<typeof messages.All>;
  countOf: (group: ItemActivityGroup) => number;
}

const STATUS_FILTERS: StatusFilterDef[] = [
  {
    id: "ALL",
    labelKey: () => messages.All(),
    countOf: (g) => g.changeRequests.length,
  },
  {
    id: CrSt.PENDING,
    labelKey: () => messages.Cr_Status_Pending(),
    countOf: (g) => g.counts.pending,
  },
  {
    id: CrSt.ACCEPTED,
    labelKey: () => messages.Cr_Status_Accepted(),
    countOf: (g) => g.counts.accepted,
  },
  {
    id: CrSt.REJECTED,
    labelKey: () => messages.Cr_Status_Rejected(),
    countOf: (g) => g.counts.rejected,
  },
  {
    id: CrSt.SUPERSEDED,
    labelKey: () => messages.Cr_Status_Superseded(),
    countOf: (g) => g.counts.superseded,
  },
  {
    id: CrSt.RESTORED,
    labelKey: () => messages.Cr_Status_Restored(),
    countOf: (g) => g.counts.restored,
  },
  {
    id: "HISTORY",
    labelKey: () => messages.History(),
    countOf: (g) => g.changeRequests.length,
  },
];

export function ItemReviewWorkbench({
  flyerId,
  reqOptions,
  bootstrap,
  flyerPublicationStatus,
  selectedFlyerItemId,
  groups,
  summaries,
  busyIds,
  setBusy,
  onActivityRefetch,
}: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [historyTarget, setHistoryTarget] = useState<HistoryTarget | null>(
    null,
  );
  const [appliedRevision, setAppliedRevision] = useState<{
    before?: Record<string, unknown>;
    after?: Record<string, unknown>;
  } | null>(null);
  const [tab, setTab] = useState<"changes" | "comments">("changes");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("ALL");

  const group = useMemo(
    () => groups.find((g) => g.flyerItemId === selectedFlyerItemId),
    [groups, selectedFlyerItemId],
  );

  const summary = selectedFlyerItemId
    ? summaries.get(selectedFlyerItemId)
    : undefined;
  const summaryLoading = summary === "loading" || summary === undefined;
  const summaryName =
    summary && summary !== "loading" && !summary.error
      ? (summary.name || "").trim()
      : "";
  const displayName = summaryName;

  const resolveSubmitter = useCrSubmitter(bootstrap?.audiences);

  const filteredChanges = useMemo(() => {
    if (!group) return [] as ReviewChangeRequest[];
    if (statusFilter === "ALL" || statusFilter === "HISTORY") {
      return group.changeRequests;
    }
    return group.changeRequests.filter((cr) => cr.status === statusFilter);
  }, [group, statusFilter]);

  const historySlots = useMemo(() => {
    if (!group) return [];
    const itemNameById = new Map<string, string | undefined>();
    if (selectedFlyerItemId) {
      itemNameById.set(selectedFlyerItemId, displayName || undefined);
    }
    return buildHistorySlots({
      changeRequests: group.changeRequests,
      itemNameById,
      filterFlyerItemId: selectedFlyerItemId,
    });
  }, [group, selectedFlyerItemId, displayName]);

  const handleAccept = useCallback(
    async (cr: ReviewChangeRequest) => {
      setBusy(cr.id, true);
      try {
        const response =
          await reviewApiService.gfFlyerReviewAdminControllerAcceptChangeRequest(
            cr.id,
            reqOptions,
          );
        const accepted = toAcceptedCr(response);
        if (accepted?.appliedFlyerItemRevision) {
          setAppliedRevision({
            before: accepted.appliedFlyerItemRevision.before ?? undefined,
            after: accepted.appliedFlyerItemRevision.after ?? undefined,
          });
        }
        await onActivityRefetch();
        await invalidateFlyerItemCaches(
          queryClient,
          collectCrAffectedItemIds(cr),
        );
        toastSuccess(t(messages.Accept()));
      } catch (error) {
        await handleReviewError(error, t);
      } finally {
        setBusy(cr.id, false);
      }
    },
    [reqOptions, onActivityRefetch, queryClient, setBusy, t],
  );

  const handleReject = useCallback(
    async (cr: ReviewChangeRequest) => {
      setBusy(cr.id, true);
      try {
        await reviewApiService.gfFlyerReviewAdminControllerRejectChangeRequest(
          cr.id,
          reqOptions,
        );
        await onActivityRefetch();
        toastSuccess(t(messages.Reject()));
      } catch (error) {
        await handleReviewError(error, t);
      } finally {
        setBusy(cr.id, false);
      }
    },
    [reqOptions, onActivityRefetch, setBusy, t],
  );

  const handleRestore = useCallback(
    async (cr: ReviewChangeRequest) => {
      setBusy(cr.id, true);
      try {
        await reviewApiService.gfFlyerReviewAdminControllerRestoreChangeRequest(
          cr.id,
          reqOptions,
        );
        await onActivityRefetch();
        await invalidateFlyerItemCaches(
          queryClient,
          collectCrAffectedItemIds(cr),
        );
        toastSuccess(t(messages.Restored()));
      } catch (error) {
        await handleReviewError(error, t);
      } finally {
        setBusy(cr.id, false);
      }
    },
    [reqOptions, onActivityRefetch, queryClient, setBusy, t],
  );

  const handleHistory = useCallback((cr: ReviewChangeRequest) => {
    if (!cr.flyerItemId || !cr.fieldKey) return;
    setHistoryTarget({
      flyerItemId: cr.flyerItemId,
      fieldKey: cr.fieldKey,
    });
  }, []);

  if (!selectedFlyerItemId || !group) {
    return (
      <Box
        data-testid="item-workbench"
        sx={{
          border: 1,
          borderColor: "divider",
          borderRadius: `${reviewTokens.radiusLg}px`,
          p: 4,
          minHeight: 360,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "background.paper",
        }}
      >
        <Stack alignItems="center" spacing={1}>
          <DifferenceOutlinedIcon
            sx={{ fontSize: 36, color: "text.disabled" }}
          />
          <Typography variant="body2" color="text.secondary" textAlign="center">
            {t(messages.Review_Select_Item_Hint())}
          </Typography>
        </Stack>
      </Box>
    );
  }

  const changesCount = group.changeRequests.length;
  const commentsCount = group.comments.length;

  return (
    <Box
      data-testid="item-workbench"
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: `${reviewTokens.radiusLg}px`,
        bgcolor: "background.paper",
        display: "flex",
        flexDirection: "column",
        minHeight: 360,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 1.75,
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
          position: "sticky",
          top: 0,
          zIndex: 1,
        }}
      >
        <Stack
          direction="row"
          spacing={1.25}
          alignItems="center"
          flexWrap="wrap"
          sx={{ rowGap: 0.5 }}
        >
          {displayName ? (
            <Typography
              variant="h6"
              component="h2"
              sx={{
                fontWeight: 600,
                fontSize: "0.9375rem",
                maxWidth: 480,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={displayName}
            >
              {displayName}
            </Typography>
          ) : (
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography
                variant="h6"
                component="h2"
                sx={{
                  fontWeight: 600,
                  fontSize: "0.9375rem",
                  fontFamily: reviewTokens.fontMono,
                  color: summaryLoading ? "text.secondary" : "text.primary",
                }}
                title={selectedFlyerItemId || ""}
              >
                {selectedFlyerItemId
                  ? `${selectedFlyerItemId.slice(0, 8)}…`
                  : t(messages.Unnamed_Item())}
              </Typography>
              {summaryLoading ? (
                <Box
                  sx={{
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    bgcolor: reviewTokens.slate300,
                    animation: "title-pulse 1.4s ease-in-out infinite",
                    "@keyframes title-pulse": {
                      "0%, 100%": { opacity: 0.35 },
                      "50%": { opacity: 1 },
                    },
                  }}
                />
              ) : null}
            </Stack>
          )}
          <Box
            sx={{
              px: 0.75,
              py: 0.25,
              borderRadius: `${reviewTokens.radiusSm}px`,
              bgcolor: reviewTokens.slate100,
            }}
            title={selectedFlyerItemId}
          >
            <Typography
              variant="caption"
              sx={{
                fontFamily: reviewTokens.fontMono,
                color: "text.secondary",
                fontSize: 11,
              }}
            >
              {selectedFlyerItemId.slice(0, 8)}…
            </Typography>
          </Box>
        </Stack>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 0.75, fontSize: "0.8125rem" }}
        >
          {t(messages.Review_Workbench_Subtitle())}
        </Typography>
      </Box>

      <Box
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          px: 2.5,
          bgcolor: "background.paper",
        }}
      >
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ minHeight: 40 }}
        >
          <Tab
            value="changes"
            sx={{ minHeight: 40, pr: 1.5 }}
            label={
              <Stack direction="row" spacing={0.75} alignItems="center">
                <DifferenceOutlinedIcon sx={{ fontSize: 16 }} />
                <span>{t(messages.Change_Requests())}</span>
                <Badge
                  badgeContent={changesCount}
                  showZero
                  sx={{
                    ml: 0.5,
                    "& .MuiBadge-badge": {
                      position: "static",
                      transform: "none",
                      bgcolor: reviewTokens.slate100,
                      color: "text.secondary",
                      fontWeight: 600,
                      fontSize: 10,
                      height: 18,
                      minWidth: 18,
                      borderRadius: `${reviewTokens.radiusSm}px`,
                      padding: "0 6px",
                    },
                  }}
                />
              </Stack>
            }
          />
          <Tab
            value="comments"
            sx={{ minHeight: 40, pr: 1.5 }}
            label={
              <Stack direction="row" spacing={0.75} alignItems="center">
                <ChatBubbleOutlineIcon sx={{ fontSize: 16 }} />
                <span>{t(messages.Comments())}</span>
                <Badge
                  badgeContent={commentsCount}
                  showZero
                  sx={{
                    ml: 0.5,
                    "& .MuiBadge-badge": {
                      position: "static",
                      transform: "none",
                      bgcolor: reviewTokens.slate100,
                      color: "text.secondary",
                      fontWeight: 600,
                      fontSize: 10,
                      height: 18,
                      minWidth: 18,
                      borderRadius: `${reviewTokens.radiusSm}px`,
                      padding: "0 6px",
                    },
                  }}
                />
              </Stack>
            }
          />
        </Tabs>
      </Box>

      <Box sx={{ p: 2.5, flex: 1, overflow: "auto" }}>
        {tab === "changes" ? (
          <>
            <Stack
              direction="row"
              spacing={0.75}
              flexWrap="wrap"
              sx={{ mb: 2, rowGap: 0.75 }}
            >
              {STATUS_FILTERS.map((f) => {
                const active = statusFilter === f.id;
                const count = f.countOf(group);
                const labelText = t(f.labelKey());
                const isHistory = f.id === "HISTORY";
                return (
                  <Chip
                    key={f.id}
                    size="small"
                    label={
                      isHistory ? labelText : `${labelText} ${count}`
                    }
                    onClick={() => setStatusFilter(f.id)}
                    variant={active ? "filled" : "outlined"}
                    data-testid={`workbench-filter-${String(f.id).toLowerCase()}`}
                    sx={{
                      cursor: "pointer",
                      bgcolor: active
                        ? isHistory
                          ? reviewTokens.brand
                          : "text.primary"
                        : "background.paper",
                      color: active ? "background.paper" : "text.secondary",
                      borderColor: active
                        ? isHistory
                          ? reviewTokens.brand
                          : "text.primary"
                        : "divider",
                      "&:hover": {
                        bgcolor: active
                          ? isHistory
                            ? reviewTokens.brandHover
                            : "text.primary"
                          : reviewTokens.slate50,
                      },
                      "& .MuiChip-label": {
                        color: active ? "background.paper" : "text.secondary",
                      },
                    }}
                  />
                );
              })}
            </Stack>

            {statusFilter === "HISTORY" ? (
              <HistoryTree
                slots={historySlots}
                flyerPublicationStatus={flyerPublicationStatus}
                resolveSubmitter={resolveSubmitter}
                busyIds={busyIds}
                onAccept={handleAccept}
                onReject={handleReject}
                onRestore={handleRestore}
              />
            ) : filteredChanges.length === 0 ? (
              <Stack alignItems="center" spacing={1} sx={{ py: 6 }}>
                <DifferenceOutlinedIcon
                  sx={{ fontSize: 28, color: "text.disabled" }}
                />
                <Typography variant="body2" color="text.secondary">
                  {t(messages.No_Change_Requests_Yet())}
                </Typography>
              </Stack>
            ) : (
              <ChangeRequestList
                changeRequests={filteredChanges}
                busyIds={busyIds}
                flyerPublicationStatus={flyerPublicationStatus}
                itemSummaries={summaries}
                reqOptions={reqOptions}
                resolveSubmitter={resolveSubmitter}
                onAccept={handleAccept}
                onReject={handleReject}
                onRestore={handleRestore}
                onHistory={handleHistory}
              />
            )}
          </>
        ) : (
          <>
            {group.comments.length === 0 ? (
              <Stack alignItems="center" spacing={1} sx={{ py: 6 }}>
                <ChatBubbleOutlineIcon
                  sx={{ fontSize: 28, color: "text.disabled" }}
                />
                <Typography variant="body2" color="text.secondary">
                  {t(messages.No_Comments_Yet())}
                </Typography>
              </Stack>
            ) : (
              <ItemCommentsList
                flyerId={flyerId}
                reqOptions={reqOptions}
                bootstrap={bootstrap}
                comments={group.comments}
              />
            )}
          </>
        )}
      </Box>

      <CrHistoryDrawer
        target={historyTarget}
        onClose={() => setHistoryTarget(null)}
        reqOptions={reqOptions}
        audiences={bootstrap?.audiences}
      />
      <AppliedRevisionPreview
        open={!!appliedRevision}
        onClose={() => setAppliedRevision(null)}
        before={appliedRevision?.before}
        after={appliedRevision?.after}
      />
    </Box>
  );
}
